import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiResponse } from '../types';
import { withRetry, RetryOptions } from './retry';

export class ApiClient {
  private client: AxiosInstance;
  private apiKey: string;
  private gameId: string;
  private isBrowser: boolean;
  private baseURL: string;
  private sessionToken: string | null = null;
  private environment: 'development' | 'staging' | 'production';
  private retryOptions: RetryOptions;

  constructor(
    apiUrl: string, 
    apiKey: string, 
    gameId: string, 
    environment: 'development' | 'staging' | 'production' = 'production',
    retryOptions: RetryOptions = {}
  ) {
    this.apiKey = apiKey;
    this.gameId = gameId;
    this.baseURL = apiUrl;
    this.environment = environment;
    this.retryOptions = {
      maxRetries: 3,
      baseDelay: 500,
      maxDelay: 5000,
      ...retryOptions
    };
    
    // Detect if we're running in a browser environment
    this.isBrowser = typeof window !== 'undefined';
    
    this.logInfo('Creating API client with base URL:', apiUrl);
    
    // Create axios instance with appropriate configuration
    this.client = axios.create({
      baseURL: apiUrl,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'x-game-id': gameId
      },
      timeout: 30000 // Increase timeout to 30 seconds for debugging
    });

    // Add request interceptor to include session token if available
    this.client.interceptors.request.use(
      config => {
        if (this.sessionToken) {
          config.headers = config.headers || {};
          config.headers['x-session-token'] = this.sessionToken;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      response => {
        this.logInfo('Received response:', response.status, response.data);
        return response;
      },
      error => {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          if(error.response.status !== 304) {
            this.logError('API Error:', error.response.status, error.response.data);
          }
          return Promise.reject(error.response.data);
        } else if (error.request) {
          // The request was made but no response was received
          this.logError('No response received:', error.request);
          this.logError('Request details:', {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
            timeout: error.config?.timeout
          });
          return Promise.reject({ error: 'No response from server' });
        } else {
          // Something happened in setting up the request that triggered an Error
          this.logError('Request error:', error.message);
          return Promise.reject({ error: error.message });
        }
      }
    );
  }

  // Set the session token to be included in all requests
  public setSessionToken(token: string | null): void {
    this.sessionToken = token;
    this.logInfo('Session token updated');
  }

  // Get the current session token
  public getSessionToken(): string | null {
    return this.sessionToken;
  }

  // Browser-safe logging method for errors
  private logError(...args: any[]): void {
    // Always log errors regardless of environment
    if (this.isBrowser) {
      console.error(...args);
    } else {
      console.error(...args);
    }
  }
  
  // Browser-safe logging method for info
  private logInfo(...args: any[]): void {
    // Only log info messages in development environment
    if (this.environment === 'development') {
      if (this.isBrowser) {
        console.log(...args);
      } else {
        console.log(...args);
      }
    }
  }

  // Method to override the API URL for testing
  public setApiUrlForTesting(apiUrl: string): void {
    this.logInfo('Overriding API URL for testing:', apiUrl);
    this.baseURL = apiUrl;
    this.client.defaults.baseURL = apiUrl;
  }

  /**
   * Get the base URL currently configured for the API client.
   */
  public getBaseURL(): string {
    return this.baseURL;
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return withRetry(async () => {
      try {
        this.logInfo('Making GET request to:', this.baseURL + url);
        const response = await this.client.get<T>(url, config);
        
        // Ensure we have a properly formatted response
        if (!response.data) {
          this.logError('API GET error: No data in response');
        }

        return response.data;
      } catch (error) {
        this.logError('API GET error:', error);
        
        // Create a standardized error response
        let errorMessage = 'Unknown error occurred';
        let errorResponse: ApiResponse<T> = { success: false, error: errorMessage };
        
        if (axios.isAxiosError(error)) {
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            errorMessage = error.response.data?.error || `Server error: ${error.response.status}`;
            errorResponse = { 
              success: false, 
              error: errorMessage,
              data: error.response.data?.data as T
            };
            
            // For 5xx errors, preserve the original error object for retry logic
            if (error.response.status >= 500 && error.response.status < 600) {
              throw error;
            }
          } else if (error.request) {
            // The request was made but no response was received
            errorMessage = 'No response received from server';
          } else {
            // Something happened in setting up the request
            errorMessage = error.message || 'Error setting up request';
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
        
        throw new Error(errorMessage);
      }
    }, this.retryOptions);
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return withRetry(async () => {
      try {
        this.logInfo('Making POST request to:', this.baseURL + url, 'with data:', data);
        const response = await this.client.post<T>(url, data, config);
        
        // Ensure we have a properly formatted response
        if (!response.data) {
          this.logError('API POST error: No data in response');
        }
        return response.data;
      } catch (error) {
        this.logError('API POST error:', error);
        
        // Create a standardized error response
        let errorMessage = 'Unknown error occurred';
        let errorResponse: ApiResponse<T> = { success: false, error: errorMessage };
        
        if (axios.isAxiosError(error)) {
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            errorMessage = error.response.data?.error || `Server error: ${error.response.status}`;
            errorResponse = { 
              success: false, 
              error: errorMessage,
              data: error.response.data?.data as T
            };
            
            // For 5xx errors, preserve the original error object for retry logic
            if (error.response.status >= 500 && error.response.status < 600) {
              throw error;
            }
          } else if (error.request) {
            // The request was made but no response was received
            errorMessage = 'No response received from server';
          } else {
            // Something happened in setting up the request
            errorMessage = error.message || 'Error setting up request';
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
        
        throw new Error(errorMessage);
      }
    }, this.retryOptions);
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return withRetry(async () => {
      try {
        this.logInfo('Making PUT request to:', this.baseURL + url, 'with data:', data);
        const response = await this.client.put<ApiResponse<T>>(url, data, config);
        return response.data;
      } catch (error) {
        this.logError('API PUT error:', error);
        
        // For 5xx errors, preserve the original error object for retry logic
        if (axios.isAxiosError(error) && error.response && 
            error.response.status >= 500 && error.response.status < 600) {
          throw error;
        }
        
        throw error;
      }
    }, this.retryOptions);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return withRetry(async () => {
      try {
        this.logInfo('Making DELETE request to:', this.baseURL + url);
        const response = await this.client.delete<ApiResponse<T>>(url, config);
        return response.data;
      } catch (error) {
        this.logError('API DELETE error:', error);
        
        // For 5xx errors, preserve the original error object for retry logic
        if (axios.isAxiosError(error) && error.response && 
            error.response.status >= 500 && error.response.status < 600) {
          throw error;
        }
        
        throw error;
      }
    }, this.retryOptions);
  }
} 